import { useState } from "react";
import {
  CRITICIDADE,
  DECISAO,
  MATRIZ_LINHAS,
  MATRIZ_VALORES,
  NATUREZA,
  POLO,
  RISCO,
  SEVERIDADE,
  SEVERIDADE_DA_FASE_2,
  SITUACAO_DO_ACHADO,
  dataHora,
  type Analise,
  type Dono,
} from "../lib/sentinela";

/**
 * As peças das telas do Sentinela (000397). Os selos são os de `badges.tsx` do app de lá, com o mesmo rótulo e a
 * mesma família de cor, nas cores SEMÂNTICAS da casa: o selo aparece sobre a superfície, que troca com o tema, e
 * cor fixa sumiria num dos dois (a regra do `index.css`). A palavra vai sempre junto da cor.
 */

const TONS = {
  erro: "border-erro-borda bg-erro-fundo text-erro-tinta",
  alerta: "border-alerta-tinta/40 bg-alerta-fundo text-alerta-tinta",
  aviso: "border-aviso-borda bg-aviso-fundo text-aviso-tinta",
  acento: "border-acento/40 bg-acento/10 text-acento",
  neutro: "border-borda bg-chip text-chip-tinta",
} as const;
type Tom = keyof typeof TONS;

export function Selo({ tom, children, riscado = false, atributo }: { tom: Tom; children: React.ReactNode; riscado?: boolean; atributo?: string }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONS[tom]} ${riscado ? "line-through" : ""}`}
      data-selo={atributo}
    >
      {children}
    </span>
  );
}

const TOM_DO_RISCO: Record<string, Tom> = { high: "erro", medium: "alerta", low: "aviso", clean: "aviso", unknown: "neutro" };
export const SeloRisco = ({ valor }: { valor: string }) => (
  <Selo tom={TOM_DO_RISCO[valor] ?? "neutro"} atributo={`risco-${valor}`}>{RISCO[valor] ?? valor}</Selo>
);

const TOM_DA_SEVERIDADE: Record<string, Tom> = { high: "erro", medium: "alerta", low: "aviso", info: "neutro" };
export const SeloSeveridade = ({ valor }: { valor: string }) => (
  <Selo tom={TOM_DA_SEVERIDADE[valor] ?? "neutro"} atributo={`severidade-${valor}`}>{SEVERIDADE[valor] ?? valor}</Selo>
);

const TOM_DA_CRITICIDADE: Record<string, Tom> = { critical: "erro", high: "alerta", medium: "acento", low: "neutro" };
export const SeloCriticidade = ({ valor }: { valor: string }) => (
  <Selo tom={TOM_DA_CRITICIDADE[valor] ?? "neutro"} atributo={`criticidade-${valor}`}>{CRITICIDADE[valor] ?? valor}</Selo>
);

const TOM_DA_SITUACAO: Record<string, Tom> = { open: "acento", confirmed: "erro", dismissed: "neutro", resolved: "aviso" };
export const SeloSituacao = ({ valor }: { valor: string }) => (
  <Selo tom={TOM_DA_SITUACAO[valor] ?? "neutro"} riscado={valor === "dismissed"} atributo={`situacao-${valor}`}>
    {SITUACAO_DO_ACHADO[valor] ?? valor}
  </Selo>
);

// "Atribuída" é azul e não vermelho, de propósito (o comentário de lá): ela descreve IDENTIDADE, não risco.
const TOM_DA_DECISAO: Record<string, Tom> = {
  ATRIBUIDA: "acento",
  ATRIBUIDA_POR_ANALISTA: "acento",
  INCONCLUSIVA: "alerta",
  NAO_ATRIBUIDA: "neutro",
};
export const SeloDecisao = ({ valor }: { valor: string }) => (
  <Selo tom={TOM_DA_DECISAO[valor] ?? "neutro"} atributo={`decisao-${valor}`}>{DECISAO[valor] ?? valor}</Selo>
);

export const SeloRevisao = () => (
  <Selo tom="alerta" atributo="revisao-humana">
    Aguardando revisão humana
  </Selo>
);

/** Um número de cartão, com o tom quando o número pede atenção (o `StatCard` de lá). */
export function Numero({ rotulo, valor, tom, atributo }: { rotulo: string; valor: number | string; tom?: "erro" | "alerta"; atributo?: string }) {
  const cor = tom === "erro" ? "text-erro-tinta" : tom === "alerta" ? "text-alerta-tinta" : "text-titulo";
  return (
    <div className="surface rounded-cf p-4" data-numero={atributo}>
      <div className={`font-display text-2xl font-bold tabular-nums ${cor}`}>{typeof valor === "number" ? valor.toLocaleString("pt-BR") : valor}</div>
      <div className="mt-1 text-xs text-cinza">{rotulo}</div>
    </div>
  );
}

/** Quem cadastrou o alvo em lá — só a leitura da equipe traz. */
export function DonoDoAlvo({ dono }: { dono: Dono }) {
  if (!dono) return null;
  return (
    <span className="text-xs text-cinza" data-dono>
      de {dono.nome || dono.email || "aluno sem nome"}
    </span>
  );
}

function percentual(v: number | string | null): string {
  const n = typeof v === "number" ? v : v === null ? NaN : Number(v);
  return Number.isFinite(n) ? `${Math.round(n * 100)}%` : "—";
}

/**
 * "Como o motor de atribuição chegou a esta decisão" — o bloco recolhível de lá (`AttributionEvidence`),
 * fechado por padrão. O que ele mostra é a trilha que a auditoria vai procurar: a matriz de compatibilidade (a
 * linha que BLOQUEIA a atribuição em destaque), a classificação de risco da fase 2, a justificativa, o trecho e
 * a fonte primária. A data da fonte vai CRUA, como lá: a mídia diz "2023" ou "há 2 meses", e formatar inventaria
 * um dia.
 */
export function Evidencia({ analise }: { analise: Analise }) {
  const [aberto, setAberto] = useState(false);
  const matriz = (analise.matriz_compatibilidade ?? {}) as Record<string, unknown>;
  const conhecidas = MATRIZ_LINHAS.map(([k]) => k);
  const chaves = [...conhecidas.filter((k) => k in matriz), ...Object.keys(matriz).filter((k) => !conhecidas.includes(k))];
  const rotulo = new Map(MATRIZ_LINHAS);
  const risco = (analise.fase_2_risco ?? null) as Record<string, string | undefined> | null;

  return (
    <div className="mt-3 rounded-cf border border-borda bg-superficie-2" data-evidencia>
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        aria-expanded={aberto}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-semibold text-cinza hover:text-tinta"
      >
        <span>Como o motor de atribuição chegou a esta decisão</span>
        <span aria-hidden="true">{aberto ? "▴" : "▾"}</span>
      </button>
      {aberto && (
        <div className="flex flex-col gap-3 border-t border-borda px-3 py-3 text-xs text-tinta" data-evidencia-aberta>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <SeloDecisao valor={analise.decisao} />
            <span className="text-cinza">
              Nível de confiança: <strong className="text-tinta">{percentual(analise.nivel_confianca)}</strong>
            </span>
            {analise.nome_do_alvo && <span className="text-cinza">Investigado: {analise.nome_do_alvo}</span>}
          </div>

          {analise.decisao === "ATRIBUIDA_POR_ANALISTA" && (
            <p className="rounded-cf border border-acento/40 bg-acento/10 px-2.5 py-1.5 text-tinta">
              O portão do motor havia respondido <strong>{analise.decisao_original ?? "outra decisão"}</strong> para este documento.
              A identidade foi confirmada por um analista{analise.revisado_em ? ` em ${dataHora(analise.revisado_em)}` : ""}, e a
              classificação de risco abaixo foi produzida pelo motor a partir dessa asserção.
            </p>
          )}

          {chaves.length > 0 && (
            <div>
              <div className="mb-1.5 font-semibold">Matriz de compatibilidade</div>
              <dl className="divide-y divide-borda rounded-cf border border-borda">
                {chaves.map((k) => {
                  const valor = String(matriz[k] ?? "");
                  const bloqueia = /^(CONTRADICAO|INCOMPATIVEL)/.test(valor.toUpperCase());
                  return (
                    <div key={k} className="flex items-center justify-between gap-3 px-2.5 py-1.5">
                      <dt className="text-cinza">{rotulo.get(k) ?? k}</dt>
                      <dd className={bloqueia ? "rounded bg-erro-fundo px-1.5 py-0.5 font-bold text-erro-tinta" : ""} data-matriz-bloqueia={bloqueia ? "" : undefined}>
                        {MATRIZ_VALORES[valor.toUpperCase()] ?? (valor || "—")}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          )}

          {risco && (
            <div>
              <div className="mb-1 font-semibold">Classificação de risco (fase 2)</div>
              <p className="text-cinza">
                Categoria: {risco.categoria || "—"}
                {risco.severidade ? ` · Severidade ${SEVERIDADE_DA_FASE_2[risco.severidade] ?? risco.severidade}` : ""}
                {" · Natureza do fato: "}
                {risco.natureza ? (NATUREZA[risco.natureza] ?? risco.natureza) : "—"}
                {" · O investigado figura no "}
                {POLO[risco.polo ?? ""] ?? `polo ${risco.polo || "indeterminado"}`}.
              </p>
              {risco.resumo && <p className="mt-1 text-cinza">{risco.resumo}</p>}
            </div>
          )}

          {analise.justificativa_estruturada && (
            <div>
              <div className="mb-1 font-semibold">Justificativa</div>
              <p className="text-cinza">{analise.justificativa_estruturada}</p>
            </div>
          )}

          {analise.evidencia_trecho && (
            <div>
              <div className="mb-1 font-semibold">Trecho usado como evidência</div>
              <blockquote className="border-l-2 border-acento/50 px-3 py-2 italic text-cinza">{analise.evidencia_trecho}</blockquote>
            </div>
          )}

          <div>
            <div className="mb-1 font-semibold">Fonte primária</div>
            {analise.titulo_da_fonte && <p>{analise.titulo_da_fonte}</p>}
            <p className="text-cinza">
              {analise.veiculo_da_fonte || "veículo não informado"}
              {analise.fonte_publicada_em ? ` · ${analise.fonte_publicada_em}` : ""}
            </p>
            {analise.url_da_fonte && (
              <a href={analise.url_da_fonte} target="_blank" rel="noopener noreferrer" className="font-semibold text-link underline">
                Abrir o documento original<span className="sr-only"> (abre em outra aba)</span>
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** O aviso de que a tela é só leitura até a virada — uma vez, no alto. */
export function AvisoDaVirada() {
  return (
    <p role="note" className="rounded-cf border border-borda bg-chip px-4 py-3 text-sm text-tinta" data-aviso-virada>
      Só leitura até a virada: os alunos usam o Sentinela no app de lá, e esta tela mostra a cópia que o hub atualiza de 10 em
      10 minutos. Cadastrar, varrer e decidir apontamentos continuam lá.
    </p>
  );
}
